import { toast } from 'react-toastify';

/**
 * DEPRECATED: matches every rejected action in the entire store.
 * Use createScopedRejectedMatcher(sliceName) instead.
 */
export const isRejectedAction = (action) => {
  return action.type.endsWith('/rejected');
};

/**
 * Creates a matcher scoped to a single slice so that only that slice's own
 * rejected actions trigger the toast — not every action in the store.
 *
 * Usage in a slice:
 *   const isRejected = createScopedRejectedMatcher('MySlice');
 *   builder.addMatcher(isRejected, handleRejectedAction);
 *
 * The slice name must match the first segment of the action type, e.g.:
 *   action.type = "MySlice/getSomething/rejected"  → matches
 *   action.type = "OtherSlice/getSomething/rejected" → does NOT match
 */
export const createScopedRejectedMatcher = (sliceName) => (action) => {
  const scopes = Array.isArray(sliceName) ? sliceName : [sliceName];
  return scopes.some((scope) => action.type.startsWith(`${scope}/`)) && action.type.endsWith('/rejected');
};

/**
 * Extract error message from different possible locations in the action payload
 * @param {Object} action - Redux action
 * @returns {string} Error message
 */
export const extractErrorMessage = (action) => {
  return (
    action.payload?.error ||
    action.payload?.message ||
    action.error?.message ||
    'An error occurred'
  );
};

/**
 * Generic rejected action handler for Redux slices
 * Shows error toast and sets loading to false
 * @param {Object} state - Redux state
 * @param {Object} action - Redux action
 */
/**
 * True when a rejected action describes a cancellation rather than a failure.
 *
 * Exported so the behaviour can be tested directly: the version of this that
 * only checked one of these shapes looked correct in the source and still put
 * an "Aborted" toast on screen.
 */
export const isCancelledAction = (action) => Boolean(
  action?.meta?.aborted ||
  action?.payload?.cancelled ||
  action?.error?.name === 'AbortError' ||
  action?.error?.name === 'CanceledError' ||
  action?.error?.message === 'Aborted' ||
  action?.error?.message === 'canceled'
);

export const handleRejectedAction = (state, action) => {
  state.loading = false;

  // A cancelled request is not a failure. Superseded searches abort by design,
  // and reporting each one puts a toast on screen for every character typed
  // into a search box.
  //
  // Three shapes, because a cancellation reaches here by three routes: RTK
  // aborting the thunk before the payload creator ever settles (meta.aborted,
  // error.name AbortError, message "Aborted"), axios raising CanceledError
  // inside it, and our own rejectWithValue marker.
  if (isCancelledAction(action)) return;

  const errorMessage = extractErrorMessage(action);

  console.error('Redux action rejected:', action.type, errorMessage);

  // Màn hình 403 đã hiển thị cho request này → thêm toast chỉ gây nhiễu.
  if (action.payload?.accessDeniedPageShown) return;

  toast.error(errorMessage, { autoClose: 5000 });
};

/**
 * Add a SCOPED rejected action matcher to a builder.
 * Only fires for actions that belong to the given slice (by name).
 *
 * Usage:
 *   addRejectedMatcher(builder, 'MySlice');
 *
 * @param {Object} builder - Redux Toolkit builder
 * @param {string|string[]} sliceName - The owned thunk namespace(s), including exact casing
 */
export const addRejectedMatcher = (builder, sliceName) => {
  const matcher = sliceName
    ? createScopedRejectedMatcher(sliceName)
    : isRejectedAction; // fallback for callers that haven't been updated yet
  builder.addMatcher(matcher, handleRejectedAction);
};

/**
 * Generic async thunk error handler
 * Extracts error payload from Axios errors
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Default error message
 * @returns {Object} Error payload for rejectWithValue
 */
/**
 * True when a request was deliberately cancelled — a superseded search, a
 * component unmounting — rather than having failed.
 *
 * Cancellation reaches the same catch block as a real failure, so without this
 * every abandoned keystroke would raise "Failed to get projects" at the user.
 */
export const isRequestCancelled = (error) =>
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.name === 'AbortError' ||
  error?.message === 'canceled';

export const handleAsyncThunkError = (error, defaultMessage = 'Operation failed') => {
  // Silent by design: a cancelled request is the expected outcome of typing the
  // next character, not something to log or report.
  if (isRequestCancelled(error)) {
    return { error: null, cancelled: true, silent: true };
  }

  console.error('Async thunk error:', error);

  // 403 — access denied, signal upstream so reducers deduplicate the toast.
  // `accessDeniedPageShown` = interceptor đã dựng màn hình 403 cho request đọc
  // này, reducer không cần bắn toast nữa.
  if (error.isAccessDenied) {
    return {
      error: error.message,
      status: 403,
      isAccessDenied: true,
      accessDeniedPageShown: Boolean(error.accessDeniedPageShown),
    };
  }

  // Handle different error structures
  const errorPayload = error.response?.data || {
    error: error.message || defaultMessage,
    status: error.response?.status,
  };

  // Handle case where error is an array of messages
  if (Array.isArray(errorPayload.error)) {
    errorPayload.error = errorPayload.error.join(', ');
  }

  return errorPayload;
};
